import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtEuro0, MONTHS_EL, MONTHS_EL_SHORT } from "@/lib/format";
import { byEmployee, byEmployeeCost, roleStripeColors, roleStripeKey } from "@/lib/sortEmployees";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { BudgetByUnit } from "@/components/BudgetByUnit";
import { BudgetLineDialog } from "@/components/BudgetLineDialog";
import { PctInput } from "@/components/UnitModeSelect";
import { HireDialog } from "@/components/HireDialog";
import { BoardRateControl } from "@/components/BoardRateControl";
import { Save, Target, TrendingDown, TrendingUp, AlertTriangle, Copy, FileSpreadsheet, Trash2, Plus, UserPlus, Layers, Pencil, Lock, LockOpen, RotateCcw } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const EXTRAS = [["easter", "Δώρο Πάσχα"], ["leave", "Επίδομα Αδείας"], ["christmas", "Δώρο Χριστ."], ["ika", "ΙΚΑ"], ["fmy", "ΦΜΥ"], ["housing", "Στέγαση"], ["feeding", "Σίτιση"]];
const AUTO_KEYS = ["easter", "leave", "christmas", "ika", "housing", "feeding"];
const RATES = { housing: 100, feeding: 100 }; // €/μήνα εργασίας, από τη ρύθμιση του server
const DAYS_IN_MONTH = { 1: 31, 2: 28, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };
const EASTER_MONTHS = [1, 2, 3, 4];
const XMAS_MONTHS = [5, 6, 7, 8, 9, 10, 11, 12];
const EASTER_DAYS = 120; // ημέρες αναφοράς Ιαν–Απρ (31+28+31+30)
const XMAS_DAYS = 245;   // ημέρες αναφοράς Μάι–Δεκ

// Αυτόματος υπολογισμός επιδομάτων από τον μηνιαίο μισθό (max μηνιαίου κελιού) & τους μήνες εργασίας.
// Πλήρες έτος: Δώρο Πάσχα 50%, Επίδομα Αδείας 50%, Δώρο Χριστουγέννων 100% του μηνιαίου μισθού.
const BOARD_KEYS = ["housing", "feeding"];
const board = (l, k, n) => (l[`${k}_override`] != null ? Number(l[`${k}_override`]) || 0 : (l[`has_${k}`] === false ? 0 : n * RATES[k]));

function computeExtras(l) {
  const worked = MONTH_KEYS.filter((m) => Number(l.months?.[m]) > 0);
  const base = worked.reduce((mx, m) => Math.max(mx, Number(l.months?.[m]) || 0), 0);
  const nMonths = worked.length;
  const daily = base / 26; // ημερομίσθιο
  const daysIn = (arr) => worked.filter((m) => arr.includes(Number(m))).reduce((s, m) => s + DAYS_IN_MONTH[Number(m)], 0);
  const easter = (base / 2) * Math.min(1, daysIn(EASTER_MONTHS) / EASTER_DAYS);      // 50% μισθού σε πλήρες έτος, αναλογικά
  const christmas = base * Math.min(1, daysIn(XMAS_MONTHS) / XMAS_DAYS);             // 100% μισθού σε πλήρες έτος, αναλογικά
  const leave = Math.min(2 * nMonths * daily, base / 2);                             // 2 ημέρες/μήνα, cap μισός μισθός (50%)
  const ika = nMonths >= 12 ? 14 * 450 : nMonths >= 1 ? 450 * (nMonths + 1.5) : 0; // ΙΚΑ: 12 μήνες→14×450, αλλιώς 450×(μήνες+1,5)
  const calc = { easter: Math.round(easter), leave: Math.round(leave), christmas: Math.round(christmas), ika,
    housing: board(l, "housing", nMonths), feeding: board(l, "feeding", nMonths) };
  // χειροκίνητη τιμή (override) υπερισχύει του αυτόματου υπολογισμού
  for (const k of ["easter", "leave", "christmas", "ika"]) if (l[`${k}_override`] != null) calc[k] = Number(l[`${k}_override`]) || 0;
  return calc;
}

const lineTotal = (l) => {
  const ex = computeExtras(l);
  return Math.round((MONTH_KEYS.reduce((s, m) => s + (Number(l.months?.[m]) || 0), 0)
    + ex.easter + ex.leave + ex.christmas + ex.ika + ex.housing + ex.feeding + (Number(l.fmy) || 0)) * 100) / 100;
};

const IkaCells = ({ r, bold }) => (
  <>
    <td className={`px-4 py-2 text-right tabular ${bold ? "font-semibold" : ""}`}>{fmtEuro(r.tax_budget || 0)}</td>
    <td className={`px-4 py-2 text-right tabular text-[#6F6A60] ${bold ? "font-semibold" : ""}`}>{fmtEuro(r.tax_actual || 0)}</td>
    <td className={`px-4 py-2 text-right tabular ${bold ? "font-semibold" : ""}`}>{fmtEuro(r.efka_budget || 0)}</td>
    <td className={`px-4 py-2 text-right tabular text-[#6F6A60] ${bold ? "font-semibold" : ""}`}>{fmtEuro(r.efka_actual || 0)}</td>
  </>
);

export default function Budget() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(2026);
  const [lines, setLines] = useState([]);
  const [roleSel, setRoleSel] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [scenOpen, setScenOpen] = useState(false);
  const [scenName, setScenName] = useState("");
  const [copyTo, setCopyTo] = useState(String(2028));
  const [copyPct, setCopyPct] = useState(5);
  const [copySource, setCopySource] = useState("budget");
  const [copyFrom, setCopyFrom] = useState(String(2026));
  const [scope, setScope] = useState("ytd");

  const { data: years = [2026, 2027] } = useQuery({ queryKey: ["budget-years"], queryFn: async () => (await api.get("/budget/years")).data });
  const { data: linesData, isLoading } = useQuery({
    queryKey: ["budget-lines", year],
    queryFn: async () => ({ year, lines: (await api.get(`/budget/lines?year=${year}`)).data }),
  });
  const [linesYear, setLinesYear] = useState(null);
  useEffect(() => {
    if (linesData) { setLines(linesData.lines); setLinesYear(linesData.year); }
  }, [linesData]);
  const linesReady = linesYear === year && !isLoading;
  const { data: compare } = useQuery({
    queryKey: ["budget-compare", year, scope],
    queryFn: async () => (await api.get(`/budget/compare?year=${year}&scope=${scope}`)).data,
  });
  const { data: alerts } = useQuery({
    queryKey: ["budget-alerts", year, scope],
    queryFn: async () => (await api.get(`/budget/alerts?year=${year}&scope=${scope}`)).data,
  });

  const copy = useMutation({
    mutationFn: async () => (await api.post("/budget/copy", {
      from_year: Number(copyFrom), to_year: Number(copyTo), increase_pct: Number(copyPct),
      source: copySource, ytd: scope === "ytd",
    })).data,
    onSuccess: (d) => {
      setCopyOpen(false);
      qc.invalidateQueries({ queryKey: ["budget-years"] });
      qc.invalidateQueries({ queryKey: ["budget-lines", d.year] });
      qc.invalidateQueries({ queryKey: ["budget-compare", d.year] });
      toast.success(`Αντιγράφηκε στο ${d.year} (${d.lines} υπάλληλοι) — σύνολο ${d.total.toLocaleString("el-GR")} €`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const [activeScen, setActiveScen] = useState(() => {
    try { return JSON.parse(localStorage.getItem("budget_active_scenario") || "null"); } catch { return null; }
  });
  const activeScenario = activeScen && activeScen.year === year ? activeScen : null;
  const setActiveScenario = (v) => {
    setActiveScen(v);
    if (v) localStorage.setItem("budget_active_scenario", JSON.stringify(v));
    else localStorage.removeItem("budget_active_scenario");
  };

  const [rate, setRate] = useState({ ...RATES });
  useQuery({
    queryKey: ["board-rate"],
    queryFn: async () => {
      const d = (await api.get("/budget/board-rate")).data;
      Object.assign(RATES, d);
      setRate({ ...d });
      return d;
    },
  });
  const onRateSaved = (d) => {
    Object.assign(RATES, d);
    setRate({ ...d });
    qc.invalidateQueries({ queryKey: ["budget-compare", year] });
    qc.invalidateQueries({ queryKey: ["budget-alerts", year] });
  };

  const save = useMutation({
    mutationFn: async (override) => {
      if (!linesReady) throw new Error("Ο προϋπολογισμός δεν έχει φορτωθεί ακόμα — περίμενε λίγο και ξαναπάτησε Αποθήκευση");
      const src = Array.isArray(override) ? override : lines;
      if (!src.some((l) => (l.name || "").trim()) && !window.confirm(`Ο προϋπολογισμός ${year} είναι άδειος. Να αποθηκευτεί άδειος;`)) throw new Error("Ακυρώθηκε");
      const payload = src.filter((l) => (l.name || "").trim()).map((l) => ({ ...l, ...computeExtras(l) }));
      const d = (await api.put("/budget/lines", {
        year, lines: payload, allow_empty: payload.length === 0,
      })).data;
      if (activeScenario && !activeLocked && payload.length) {
        try {
          await api.post(`/budget/scenarios/${activeScenario.id}/sync`);
        } catch (e) {
          if (e?.response?.status === 404) setActiveScenario(null);
          else throw e;
        }
      }
      return d;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["budget-lines", year] });
      qc.invalidateQueries({ queryKey: ["budget-compare", year] });
      qc.invalidateQueries({ queryKey: ["budget-years"] });
      scenarios.refetch();
      toast.success(activeScenario && !activeLocked
        ? `Αποθηκεύτηκε στο σενάριο «${activeScenario.name}»`
        : (activeLocked
          ? `Ο προϋπολογισμός αποθηκεύτηκε — το σενάριο «${activeScenario.name}» είναι κλειδωμένο και δεν άλλαξε`
          : "Ο προϋπολογισμός αποθηκεύτηκε — τα σύνολα ενημερώθηκαν"));
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const setCell = (idx, field, val, isMonth) => {
    const next = [...lines];
    const l = { ...next[idx] };
    if (isMonth) l.months = { ...(l.months || {}), [field]: Number(val) || 0 };
    else l[field] = field === "taf_pct" ? val : Number(val) || 0;
    next[idx] = l;
    setLines(next);
  };

  const setField = (idx, field, val) => {
    const next = [...lines];
    next[idx] = { ...next[idx], [field]: val };
    setLines(next);
  };

  const removeLine = (idx) => {
    const l = lines[idx];
    if (!window.confirm(`Διαγραφή της γραμμής «${l.name || "χωρίς όνομα"}» από τον προϋπολογισμό ${year}; Πάτησε μετά Αποθήκευση.`)) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const scenarios = useQuery({
    queryKey: ["budget-scenarios", year],
    queryFn: async () => (await api.get(`/budget/scenarios?year=${year}`)).data,
  });

  const activeLocked = !!(scenarios.data || []).find((s) => s.id === activeScenario?.id)?.locked;

  const toggleLock = useMutation({
    mutationFn: async ({ id, locked }) => (await api.put(`/budget/scenarios/${id}`, { locked })).data,
    onSuccess: (d) => {
      scenarios.refetch();
      toast.success(d.locked ? `Το σενάριο «${d.name}» κλειδώθηκε` : `Το σενάριο «${d.name}» ξεκλειδώθηκε`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const saveScenario = useMutation({
    mutationFn: async () => (await api.post("/budget/scenarios", { year, name: scenName })).data,
    onSuccess: (d) => {
      scenarios.refetch();
      setScenName("");
      setActiveScenario({ id: d.id, name: d.name, year });
      toast.success(`Το σενάριο «${d.name}» αποθηκεύτηκε και είναι ενεργό`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const applyScenario = useMutation({
    mutationFn: async (id) => ({ ...(await api.post(`/budget/scenarios/${id}/apply`)).data, id }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["budget-lines"] });
      qc.invalidateQueries({ queryKey: ["budget-compare"] });
      scenarios.refetch();
      setActiveScenario({ id: d.id, name: d.applied, year: d.year });
      toast.success(`Φορτώθηκε το «${d.applied}» (${d.lines} γραμμές) — οι αλλαγές θα αποθηκεύονται εδώ`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const revertScenario = useMutation({
    mutationFn: async (id) => {
      const d = (await api.post(`/budget/scenarios/${id}/revert`)).data;
      if (activeScenario?.id === id) await api.post(`/budget/scenarios/${id}/apply`);
      return d;
    },
    onSuccess: (d) => {
      scenarios.refetch();
      if (activeScenario?.id === d.id) {
        qc.invalidateQueries({ queryKey: ["budget-lines"] });
        qc.invalidateQueries({ queryKey: ["budget-compare"] });
      }
      toast.success(`Το «${d.name}» επανήλθε στην προηγούμενη αποθήκευση (${d.line_count} γραμμές, ${fmtEuro0(d.total)})`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const renameScenario = useMutation({
    mutationFn: async ({ id, name }) => (await api.put(`/budget/scenarios/${id}`, { name })).data,
    onSuccess: (d) => {
      scenarios.refetch();
      if (activeScenario?.id === d.id) setActiveScenario({ ...activeScenario, name: d.name });
      toast.success("Μετονομάστηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const deleteScenario = useMutation({
    mutationFn: async (id) => ({ ...(await api.delete(`/budget/scenarios/${id}`)).data, id }),
    onSuccess: (d) => {
      scenarios.refetch();
      if (activeScenario?.id === d.id) setActiveScenario(null);
      toast.success("Το σενάριο διαγράφηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const [dlg, setDlg] = useState({ open: false, idx: null });
  const submitLine = (ln) => {
    if (dlg.idx == null) setLines([...lines, ln]);
    else setLines(lines.map((x, j) => (j === dlg.idx ? ln : x)));
    setDlg({ open: false, idx: null });
    toast.success(`${dlg.idx == null ? "Προστέθηκε" : "Ενημερώθηκε"}: ${ln.name} — πάτησε Αποθήκευση`);
  };
  const [hireIdx, setHireIdx] = useState(null);
  const hire = useMutation({
    mutationFn: async (name) => {
      const l = lines[hireIdx];
      const wm = MONTH_KEYS.filter((m) => Number(l.months?.[m]) > 0);
      const taf = l.taf_pct ?? 1;
      const existing = (employees || []).find((e) => (e.name || "").trim() === name);
      const emp = existing || (await api.post("/employees", {
        name, department: l.department || "", position: l.position || "",
        monthly_base: wm.reduce((mx, m) => Math.max(mx, Number(l.months[m]) || 0), 0),
        work_months: wm.map(Number), taf_pct: taf, tribe_pct: Math.round((1 - taf) * 10000) / 10000,
        accommodation: l.has_housing !== false, meals: l.has_feeding !== false, active: true,
      })).data;
      const next = lines.map((x, j) => (j === hireIdx ? { ...x, name: emp.name, code: emp.code } : x));
      setLines(next);
      await save.mutateAsync(next);
      return emp;
    },
    onSuccess: (emp) => {
      setHireIdx(null);
      qc.invalidateQueries({ queryKey: ["employees-for-budget"] });
      toast.success(`Ο/Η ${emp.name} προσλήφθηκε (κωδ. ${emp.code}) — δείτε την καρτέλα στη Διαχείριση Προσωπικού`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const departments = useMemo(() => Array.from(new Set(lines.map((l) => l.department).filter(Boolean))).sort(), [lines]);
  const positions = useMemo(() => Array.from(new Set(lines.map((l) => l.position).filter(Boolean))).sort(), [lines]);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees-for-budget"],
    queryFn: async () => (await api.get("/employees")).data,
    enabled: canEdit,
  });
  const empNames = useMemo(() => new Set((employees || []).map((e) => (e.name || "").trim())), [employees]);
  const isVacancy = (l) => !l.code && (l.name || "").trim() && !empNames.has((l.name || "").trim());

  const availableEmployees = useMemo(() => {
    const used = new Set(lines.map((l) => (l.name || "").trim()));
    return (employees || [])
      .filter((e) => !used.has((e.name || "").trim()))
      .sort(byEmployee);
  }, [employees, lines]);


  const allRoles = useMemo(
    () => Array.from(new Set(lines.map((l) => l.position || l.department).filter(Boolean))).sort(),
    [lines]
  );
  const visible = useMemo(() => {
    const cmp = byEmployeeCost(lineTotal);
    return lines
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => roleSel.length === 0 || roleSel.includes(l.position || l.department))
      .sort((a, b) => cmp(a.l, b.l));
  }, [lines, roleSel]);

  const deptColors = useMemo(() => roleStripeColors(visible.map((v) => v.l)), [visible]);

  const colTotals = useMemo(() => {
    const t = { months: {}, extras: {}, total: 0 };
    MONTH_KEYS.forEach((m) => { t.months[m] = lines.reduce((s, l) => s + (Number(l.months?.[m]) || 0), 0); });
    EXTRAS.forEach(([k]) => {
      t.extras[k] = lines.reduce((s, l) => s + (AUTO_KEYS.includes(k) ? computeExtras(l)[k] : (Number(l[k]) || 0)), 0);
    });
    t.total = lines.reduce((s, l) => s + lineTotal(l), 0);
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, rate]);

  const roleGroups = useMemo(() => {
    const groups = new Map();
    (compare?.role_rows || []).forEach((r) => {
      const g = groups.get(r.department) || { department: r.department, budget: 0, actual: 0, roles: [] };
      ["budget", "actual", "ika_budget", "ika_actual", "tax_budget", "tax_actual", "efka_budget", "efka_actual"].forEach((k) => { g[k] = (g[k] || 0) + (r[k] || 0); });
      g.roles.push(r);
      groups.set(r.department, g);
    });
    return Array.from(groups.values())
      .map((g) => ({ ...g, variance: Math.round((g.budget + g.ika_budget - g.actual - g.ika_actual) * 100) / 100 }))
      .sort((a, b) => b.budget - a.budget);
  }, [compare]);

  const chartData = (compare?.rows || []).map((r) => ({ name: r.label, "Προϋπολογισμός": r.budget, "Πραγματικό": r.actual }));
  const varTotal = compare?.variance_total || 0;
  const overBudget = varTotal < 0;
  const cell = "w-[52px] rounded border border-[#E3DED4] px-1 py-1 text-xs text-right tabular outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="budget-page" data-wide>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Οικονομικός Σχεδιασμός</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Προϋπολογισμός & Παρακολούθηση</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Προϋπολογισμός ανά υπάλληλο και ανά μήνα — τα μηνιαία και ετήσια σύνολα προκύπτουν αυτόματα.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Έτος</label>
            <select data-testid="budget-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="rounded-xl border border-[#E3DED4] bg-white p-1 flex" data-testid="budget-scope-toggle">
            {[["ytd", `Μέχρι σήμερα${scope === "ytd" && compare?.scope_label ? ` (${compare.scope_label})` : ""}`], ["year", "Όλο το έτος"]].map(([v, l]) => (
              <button key={v} data-testid={`budget-scope-${v}`} onClick={() => setScope(v)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  scope === v ? "bg-[#45423C] text-white" : "text-[#6F6A60] hover:bg-[#F1EDE6]"}`}>
                {l}
              </button>
            ))}
          </div>
          {canEdit && (
            <button data-testid="budget-scenarios-open" onClick={() => setScenOpen((v) => !v)} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
              <Layers className="h-4 w-4" /> Σενάρια
            </button>
          )}
          {canEdit && (
            <button data-testid="budget-copy-open" onClick={() => setCopyOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
              <Copy className="h-4 w-4" /> Αντιγραφή σε έτος…
            </button>
          )}
          <button data-testid="budget-export-xlsx" onClick={() => downloadXlsx(`/export/budget.xlsx?year=${year}`, `TAF_Proypologismos_${year}.xlsx`)}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          {canEdit && (
            <button data-testid="budget-save" onClick={() => save.mutate()} disabled={save.isPending || !linesReady} className="inline-flex items-center gap-2 rounded-xl bg-[#6E7A63] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : (activeScenario && !activeLocked ? `Αποθήκευση στο «${activeScenario.name}»` : "Αποθήκευση")}
            </button>
          )}
        </div>
      </div>

      {scenOpen && canEdit && (
        <div className="rounded-2xl border border-[#E3DED4] bg-white p-5 space-y-4" data-testid="budget-scenarios-panel">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-[#45423C]">Σενάρια προϋπολογισμού {year}</h2>
              <p className="text-xs text-[#6F6A60] mt-1">Αποθήκευσε την τρέχουσα εικόνα με όνομα και φόρτωσέ την όποτε θέλεις. Πριν από κάθε φόρτωση κρατάμε αυτόματο αντίγραφο.</p>
              {activeScenario && (
                <p className="text-xs text-[#6E7A63] mt-1.5 font-semibold" data-testid="active-scenario-note">
                  Ενεργό σενάριο: «{activeScenario.name}»{activeLocked ? " — ΚΛΕΙΔΩΜΕΝΟ: η Αποθήκευση δεν το αλλάζει." : " — η «Αποθήκευση» ενημερώνει αυτό το σενάριο."}
                  <button data-testid="scenario-detach" onClick={() => { setActiveScenario(null); toast.success("Αποσυνδέθηκε — η Αποθήκευση δεν ενημερώνει σενάριο"); }}
                    className="ml-2 underline text-[#6F6A60] hover:text-[#45423C]">αποσύνδεση</button>
                </p>
              )}
            </div>
            <div className="flex items-end gap-2">
              <input data-testid="scenario-name" value={scenName} onChange={(e) => setScenName(e.target.value)}
                placeholder="π.χ. Βάση 2027 / Αισιόδοξο +10%"
                className="w-64 rounded-xl border border-[#E3DED4] px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
              <button data-testid="scenario-save" disabled={!scenName.trim() || saveScenario.isPending}
                onClick={() => saveScenario.mutate()}
                className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Save className="h-4 w-4" /> Αποθήκευση ως νέο σενάριο
              </button>
            </div>
          </div>
          {(scenarios.data || []).length === 0 ? (
            <p className="text-sm text-[#6F6A60]">Δεν έχεις αποθηκεύσει σενάρια για το {year} ακόμη.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left text-[10px] uppercase tracking-wider text-[#6F6A60]">
                    <th className="px-3 py-2">Όνομα</th>
                    <th className="px-3 py-2 text-right">Σύνολο</th>
                    <th className="px-3 py-2 text-right">Γραμμές</th>
                    <th className="px-3 py-2">Δημιουργήθηκε</th>
                    <th className="px-3 py-2 text-right">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody>
                  {(scenarios.data || []).map((s) => (
                    <tr key={s.id} data-testid={`scenario-row-${s.id}`} className={`border-t border-[#E3DED4] hover:bg-[#F7F5F1] ${activeScenario?.id === s.id ? "bg-[#F1F4EE]" : ""}`}>
                      <td className="px-3 py-2 font-medium text-[#45423C]">
                        {s.name}
                        {activeScenario?.id === s.id && <span className="ml-2 rounded-full bg-[#6E7A63] px-2 py-0.5 text-[10px] font-semibold text-white">ενεργό</span>}
                        {s.locked && <span data-testid={`scenario-locked-${s.id}`} className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F1EDE6] px-2 py-0.5 text-[10px] font-semibold text-[#45423C]"><Lock className="h-3 w-3" /> κλειδωμένο</span>}
                        {s.auto && <span className="ml-2 rounded-full bg-[#F1EDE6] px-2 py-0.5 text-[10px] text-[#6F6A60]">αυτόματο</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular">{fmtEuro0(s.total)}</td>
                      <td className="px-3 py-2 text-right tabular text-[#6F6A60]">{s.line_count}</td>
                      <td className="px-3 py-2 text-xs text-[#6F6A60]">
                        {new Date(s.created_at).toLocaleString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} · {s.created_by}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1.5">
                          <button data-testid={`scenario-apply-${s.id}`}
                            onClick={() => { if (window.confirm(`Να φορτωθεί το σενάριο «${s.name}» στον προϋπολογισμό ${year}; Ο τρέχων θα κρατηθεί ως αυτόματο αντίγραφο.`)) applyScenario.mutate(s.id); }}
                            className="rounded-lg bg-[#6E7A63] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90">
                            Φόρτωση
                          </button>
                          {s.has_prev && (
                            <button data-testid={`scenario-revert-${s.id}`} title={s.locked ? "Κλειδωμένο" : "Επαναφορά προηγούμενης αποθήκευσης"} disabled={s.locked || revertScenario.isPending}
                              onClick={() => { if (window.confirm(`Να επανέλθει το «${s.name}» στην προηγούμενη αποθήκευσή του;${activeScenario?.id === s.id ? " Είναι το ενεργό σενάριο, οπότε θα φορτωθεί και στον προϋπολογισμό (ο τρέχων κρατιέται ως αυτόματο αντίγραφο)." : ""} Μπορείς να το αναιρέσεις ξαναπατώντας αυτό το κουμπί.`)) revertScenario.mutate(s.id); }}
                              className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A] disabled:opacity-40">
                              <RotateCcw className="h-3.5 w-3.5" /> Προηγούμενη
                            </button>
                          )}
                          <button data-testid={`scenario-lock-${s.id}`} title={s.locked ? "Ξεκλείδωμα" : "Κλείδωμα (προστασία από αλλαγές)"}
                            onClick={() => toggleLock.mutate({ id: s.id, locked: !s.locked })}
                            className={`p-1.5 rounded-lg ${s.locked ? "text-[#45423C] bg-[#F1EDE6]" : "text-[#6F6A60] hover:bg-[#F1EDE6]"}`}>
                            {s.locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                          </button>
                          <button data-testid={`scenario-rename-${s.id}`} title={s.locked ? "Κλειδωμένο" : "Μετονομασία"} disabled={s.locked}
                            onClick={() => { const n = window.prompt("Νέο όνομα σεναρίου", s.name); if (n && n.trim() !== s.name) renameScenario.mutate({ id: s.id, name: n.trim() }); }}
                            className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6] disabled:opacity-40 disabled:hover:bg-transparent">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button data-testid={`scenario-delete-${s.id}`} title={s.locked ? "Κλειδωμένο" : "Διαγραφή"} disabled={s.locked}
                            onClick={() => { if (window.confirm(`Διαγραφή του σεναρίου «${s.name}»;`)) deleteScenario.mutate(s.id); }}
                            className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B] disabled:opacity-40 disabled:hover:bg-transparent">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {copyOpen && (
        <div className="rounded-2xl border border-[#B9AE9A] bg-[#FFFBEB] p-5" data-testid="budget-copy-panel">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Πηγή</label>
              <select data-testid="budget-copy-source" value={copySource} onChange={(e) => setCopySource(e.target.value)}
                className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
                <option value="budget">Προϋπολογισμός</option>
                <option value="actual">Πραγματικό κόστος (μισθοδοσίες)</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Από έτος</label>
              <select data-testid="budget-copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
                className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
                {Array.from(new Set([...years, 2025, 2026, 2027])).sort().map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Προς έτος</label>
              <input data-testid="budget-copy-to" type="number" value={copyTo} onChange={(e) => setCopyTo(e.target.value)}
                className="mt-1 block w-28 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Αύξηση %</label>
              <input data-testid="budget-copy-pct" type="number" step="0.5" value={copyPct} onChange={(e) => setCopyPct(e.target.value)}
                className="mt-1 block w-28 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm text-right outline-none focus:border-[#45423C]" />
            </div>
            <button data-testid="budget-copy-confirm" onClick={() => copy.mutate()} disabled={copy.isPending}
              className="rounded-xl bg-[#C86235] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {copy.isPending ? "Αντιγραφή…" : "Αντιγραφή"}
            </button>
            <button data-testid="budget-copy-cancel" onClick={() => setCopyOpen(false)} className="text-sm text-[#6F6A60] hover:text-[#45423C] py-2.5">Άκυρο</button>
          </div>
          <p className="text-xs text-[#6F6A60] mt-3">
            {copySource === "actual"
              ? `Αντιγράφει το ΠΡΑΓΜΑΤΙΚΟ κόστος μισθοδοσίας του ${copyFrom}${scope === "ytd" ? " μέχρι σήμερα" : " (όλο το έτος)"} ως προϋπολογισμό του ${copyTo}, με την αύξηση που ορίζετε.`
              : `Αντιγράφει τον προϋπολογισμό του ${copyFrom} στο ${copyTo} με την αύξηση που ορίζετε.`} ΦΜΥ & ΕΦΚΑ κάθε υπαλλήλου = ετήσιο πραγματικό σύνολο από τις μισθοδοσίες του {copyFrom}.
            {" Τυχόν υπάρχων προϋπολογισμός του έτους προορισμού αντικαθίσταται."}
          </p>
        </div>
      )}

      {alerts?.count > 0 && (
        <div className="rounded-2xl border border-[#991B1B]/30 bg-[#FEF2F2] p-5" data-testid="budget-alerts">
          <div className="flex items-center gap-2 text-[#991B1B]">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-semibold">
              Υπερβάσεις προϋπολογισμού {year} · {compare?.scope === "ytd" ? `μέχρι σήμερα (${compare?.scope_label})` : "όλο το έτος"}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            {[["Μήνες", alerts.months], ["Τμήματα", alerts.departments], ["Ρόλοι", alerts.roles || []], ["Υπάλληλοι", alerts.employees]].map(([title, list]) => (
              <div key={title}>
                <div className="text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">{title} ({list.length})</div>
                {list.length === 0 ? <div className="text-[#6F6A60] text-xs">Καμία υπέρβαση</div> : (
                  <ul className="space-y-1">
                    {list.slice(0, 6).map((a) => (
                      <li key={a.name} data-testid={`alert-${a.type}-${a.name}`} className="flex justify-between gap-3">
                        <span className="text-[#45423C]">{a.name}</span>
                        <span className="tabular font-semibold text-[#991B1B]">+{fmtEuro(a.over)}</span>
                      </li>
                    ))}
                    {list.length > 6 && <li className="text-xs text-[#6F6A60]">+{list.length - 6} ακόμη</li>}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-[#45423C] text-white p-6" data-testid="budget-total-card">
          <div className="flex items-center gap-2 text-[#8A8577]"><Target className="h-4 w-4" /><span className="text-xs uppercase tracking-widest">Προϋπολογισμός {year} {compare?.scope === "ytd" ? `· ${compare?.scope_label}` : ""}</span></div>
          <div className="font-serif text-3xl mt-2 tabular">{linesReady ? fmtEuro(colTotals.total) : "…"}</div>
          <div className="text-[11px] text-white/60 mt-1">{linesReady ? `Άθροισμα ${lines.length} υπαλλήλων` : "Φόρτωση…"}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-6">
          <div className="text-xs uppercase tracking-widest text-[#6F6A60]">Πραγματικό {year} {compare?.scope === "ytd" ? `· ${compare?.scope_label}` : ""}</div>
          <div className="font-serif text-3xl mt-2 text-[#45423C] tabular">{fmtEuro(compare?.actual_total)}</div>
        </div>
        <div className={`rounded-2xl p-6 text-white ${overBudget ? "bg-[#991B1B]" : "bg-[#6E7A63]"}`} data-testid="budget-variance-card">
          <div className="flex items-center gap-2 text-white/70">
            {overBudget ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            <span className="text-xs uppercase tracking-widest">{overBudget ? "Υπέρβαση" : "Απόκλιση (θετική)"}</span>
          </div>
          <div className="font-serif text-3xl mt-2 tabular">{fmtEuro(Math.abs(varTotal))}</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="budget-lines-card">
        <div className="px-5 py-4 border-b border-[#E3DED4] space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium text-[#45423C]">Προϋπολογισμός ανά υπάλληλο & μήνα</h2>
            <span className="text-xs text-[#6F6A60]">{canEdit ? "Συμπληρώστε τα κελιά (και το ΙΚΑ) — τα σύνολα υπολογίζονται αυτόματα. Οι αλλαγές ισχύουν μετά την Αποθήκευση." : "Προβολή μόνο"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="budget-role-filters">
            <span className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold mr-1">Ρόλος</span>
            <button data-testid="budget-role-all" onClick={() => setRoleSel([])}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${roleSel.length === 0 ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>Όλοι</button>
            {allRoles.map((rl) => (
              <button key={rl} data-testid={`budget-role-chip-${rl}`}
                onClick={() => setRoleSel(roleSel.includes(rl) ? roleSel.filter((x) => x !== rl) : [...roleSel, rl])}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${roleSel.includes(rl) ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>{rl}</button>
            ))}
            <span className="text-xs text-[#6F6A60] ml-2">{visible.length} / {lines.length}</span>
            <div className="ml-4"><BoardRateControl rate={rate} canEdit={canEdit} onSaved={onRateSaved} /></div>
            {canEdit && (
              <div className="ml-auto flex items-center gap-2">
                <button data-testid="budget-add-line" onClick={() => setDlg({ open: true, idx: null })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#45423C] text-white px-4 py-2 text-xs font-semibold hover:opacity-90">
                  <Plus className="h-3.5 w-3.5" /> Προσθήκη θέσης / υπαλλήλου
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? <div className="p-10 text-center text-[#6F6A60]">Φόρτωση…</div> : (
            <table className="text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                  <th className="px-2 py-3 sticky left-0 bg-[#45423C] z-10">Υπάλληλος</th>
                  <th className="px-2 py-3">Τμήμα</th>
                  <th className="px-2 py-3">Ρόλος</th>
                  {MONTH_KEYS.map((m) => <th key={m} className="px-1 py-3 text-right">{MONTHS_EL_SHORT[Number(m)]}</th>)}
                  {EXTRAS.map(([k, l]) => <th key={k} className="px-1 py-3 text-right">{l}</th>)}
                  <th className="px-2 py-3 text-right">Σύνολο</th>
                  <th className="px-1 py-3 text-right" title="Ποσοστό κόστους στο TAF Villas">TAF %</th>
                  <th className="px-1 py-3 text-right" title="Ποσοστό κόστους στο TRIBE Beach">TRIBE %</th>
                  {canEdit && <th className="px-2 py-3" />}
                </tr>
              </thead>
              <tbody>
                {visible.map(({ l, i }) => {
                  const rowBg = deptColors[roleStripeKey(l)] === 1 ? "bg-[#F4F1EA]" : "bg-white";
                  const ex = computeExtras(l);
                  return (
                  <tr key={l.name + i} data-testid={`budget-line-${l.name}`} className={`border-t border-[#E3DED4] hover:bg-[#F7F5F1] ${rowBg}`}>
                    <td className={`px-2 py-1.5 font-medium text-[#45423C] sticky left-0 z-10 ${rowBg}`}>
                      {canEdit ? (
                        <input data-testid={`budget-name-${l.name}`} value={l.name}
                          onChange={(e) => setField(i, "name", e.target.value)}
                          className="w-[180px] rounded border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-1 py-0.5 text-xs font-medium outline-none bg-transparent" />
                      ) : l.name}
                    </td>
                    <td className="px-2 py-1.5 text-[#6F6A60]">
                      {canEdit ? (
                        <input data-testid={`budget-dept-${l.name}`} value={l.department || ""}
                          onChange={(e) => setField(i, "department", e.target.value)}
                          className="w-[62px] rounded border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-1 py-0.5 text-xs outline-none bg-transparent" />
                      ) : (l.department || "—")}
                    </td>
                    <td className="px-2 py-1.5 text-[#6F6A60]">
                      {canEdit ? (
                        <input data-testid={`budget-role-${l.name}`} value={l.position || ""}
                          onChange={(e) => setField(i, "position", e.target.value)}
                          className="w-[62px] rounded border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-1 py-0.5 text-xs outline-none bg-transparent" />
                      ) : (l.position || "—")}
                    </td>
                    {MONTH_KEYS.map((m) => (
                      <td key={m} className="px-1 py-1.5 text-right tabular">
                        {canEdit ? (
                          <input data-testid={`budget-cell-${l.name}-${m}`} type="number" step="1" className={cell}
                            value={Math.round(Number(l.months?.[m]) || 0)} onChange={(e) => setCell(i, m, e.target.value, true)} />
                        ) : fmtEuro0(l.months?.[m])}
                      </td>
                    ))}
                    {EXTRAS.map(([k]) => (
                      <td key={k} className="px-1 py-1.5 text-right tabular">
                        {(BOARD_KEYS.includes(k) || ["easter", "leave", "christmas", "ika"].includes(k)) && canEdit ? (
                          <input data-testid={`budget-extra-${l.name}-${k}`} type="number" step="1" placeholder="auto"
                            title={BOARD_KEYS.includes(k) ? `Άδειο κελί = αυτόματο (${rate[k]} €/μήνα)` : "Άδειο κελί = αυτόματος υπολογισμός"}
                            className={`${cell} ${l[`${k}_override`] != null ? "bg-[#FFFBEB] border-[#B9AE9A]" : ""}`}
                            value={l[`${k}_override`] != null ? l[`${k}_override`] : Math.round(ex[k])}
                            onChange={(e) => setField(i, `${k}_override`, e.target.value === "" ? null : Number(e.target.value))} />
                        ) : AUTO_KEYS.includes(k) ? (
                          <span data-testid={`budget-extra-${l.name}-${k}`} className="text-[#6F6A60]" title="Αυτόματος υπολογισμός">{fmtEuro0(ex[k])}</span>
                        ) : canEdit ? (
                          <input data-testid={`budget-extra-${l.name}-${k}`} type="number" step="1" className={cell}
                            value={Math.round(Number(l[k]) || 0)} onChange={(e) => setCell(i, k, e.target.value, false)} />
                        ) : fmtEuro0(l[k])}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right tabular font-semibold text-[#45423C]">{fmtEuro0(lineTotal(l))}</td>
                    <td className="px-1 py-1.5 text-right tabular">
                      {canEdit ? (
                        <PctInput testid={`budget-taf-${l.name}`} className={cell}
                          pct={l.taf_pct ?? 1} onChange={(v) => setCell(i, "taf_pct", v, false)} />
                      ) : `${Math.round((l.taf_pct ?? 1) * 100)}%`}
                    </td>
                    <td className="px-1 py-1.5 text-right tabular">
                      {canEdit ? (
                        <PctInput testid={`budget-tribe-${l.name}`} className={cell}
                          pct={Math.round((1 - (l.taf_pct ?? 1)) * 10000) / 10000} onChange={(v) => setCell(i, "taf_pct", Math.round((1 - v) * 10000) / 10000, false)} />
                      ) : `${Math.round((1 - (l.taf_pct ?? 1)) * 100)}%`}
                    </td>
                    {canEdit && (
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        {isVacancy(l) && (
                          <button data-testid={`budget-hire-${l.name}`} title="Πρόσληψη: μετατροπή σε υπάλληλο"
                            onClick={() => setHireIdx(i)}
                            className="p-1 rounded-md text-[#6E7A63] hover:bg-[#EEF1EA]">
                            <UserPlus className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button data-testid={`budget-edit-${l.name || i}`} title="Επεξεργασία"
                          onClick={() => setDlg({ open: true, idx: i })}
                          className="p-1 rounded-md text-[#6F6A60] hover:bg-[#F1EDE6] hover:text-[#45423C]">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`budget-delete-${l.name || i}`} title="Διαγραφή γραμμής"
                          onClick={() => removeLine(i)}
                          className="p-1 rounded-md text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                  <td className="px-2 py-3 sticky left-0 bg-[#F1EDE6] z-10">ΣΥΝΟΛΟ</td>
                  <td />
                  <td />
                  {MONTH_KEYS.map((m) => <td key={m} data-testid={`budget-month-total-${m}`} className="px-1 py-3 text-right tabular">{fmtEuro0(colTotals.months[m])}</td>)}
                  {EXTRAS.map(([k]) => <td key={k} className="px-1 py-3 text-right tabular">{fmtEuro0(colTotals.extras[k])}</td>)}
                  <td className="px-2 py-3 text-right tabular" data-testid="budget-grand-total">{fmtEuro0(colTotals.total)}</td>
                  <td colSpan={2} className="px-1 py-3 text-right text-[11px] tabular" data-testid="budget-bu-split">
                    TAF {fmtEuro0(lines.reduce((s, l) => s + lineTotal(l) * (l.taf_pct ?? 1), 0))}<br />TRIBE {fmtEuro0(lines.reduce((s, l) => s + lineTotal(l) * (1 - (l.taf_pct ?? 1)), 0))}
                  </td>
                  {canEdit && <td />}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      <BudgetLineDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))}
        line={dlg.idx != null ? lines[dlg.idx] : null} employees={availableEmployees}
        departments={departments} roles={positions} rate={rate} onSubmit={submitLine} />

      <HireDialog line={hireIdx != null ? lines[hireIdx] : null} busy={hire.isPending}
        onOpenChange={(o) => { if (!o) setHireIdx(null); }} onConfirm={(name) => hire.mutate(name)} />

      <BudgetByUnit lines={linesReady ? lines : []} lineTotal={lineTotal} compare={compare} monthKeys={MONTH_KEYS} />

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 md:p-6">
        <h2 className="text-lg font-medium text-[#45423C] mb-6">Προϋπολογισμός vs Πραγματικό (ανά μήνα)</h2>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6F6A60" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#6F6A60" }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E3DED4" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Προϋπολογισμός" fill="#B9AE9A" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Πραγματικό" fill="#C86235" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E3DED4]"><h2 className="text-lg font-medium text-[#45423C]">Ανά μήνα</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-3">Μήνας</th>
                  <th className="px-4 py-3 text-right">Προϋπ.</th>
                  <th className="px-4 py-3 text-right">Πραγμ.</th>
                  <th className="px-4 py-3 text-right">Φόρος Π/Υ</th><th className="px-4 py-3 text-right">Φόρος Πραγμ.</th><th className="px-4 py-3 text-right">ΕΦΚΑ Π/Υ</th>
                  <th className="px-4 py-3 text-right">ΕΦΚΑ Πραγμ.</th>
                  <th className="px-4 py-3 text-right">Απόκλιση</th>
                </tr>
              </thead>
              <tbody>
                {(compare?.rows || []).map((r, i) => (
                  <tr key={MONTHS_EL[i + 1]} data-testid={`budget-row-${i + 1}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                    <td className="px-4 py-2 font-medium text-[#45423C]">{MONTHS_EL[i + 1]}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtEuro(r.budget)}</td>
                    <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.actual)}</td>
                    <IkaCells r={r} />
                    <td className={`px-4 py-2 text-right tabular font-medium ${r.variance < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#45423C] bg-[#F1EDE6] font-semibold text-[#45423C]">
                  <td className="px-4 py-3">ΣΥΝΟΛΟ (με δώρα & ΦΜΥ)</td>
                  <td className="px-4 py-3 text-right tabular">{fmtEuro((compare?.budget_total || 0) - (compare?.ika_budget || 0))}</td>
                  <td className="px-4 py-3 text-right tabular">{fmtEuro((compare?.actual_total || 0) - (compare?.ika_actual || 0))}</td>
                  <td className="px-4 py-3 text-right tabular" data-testid="budget-tax-budget-total">{fmtEuro(compare?.tax_budget)}</td>
                  <td className="px-4 py-3 text-right tabular" data-testid="budget-tax-actual-total">{fmtEuro(compare?.tax_actual)}</td>
                  <td className="px-4 py-3 text-right tabular" data-testid="budget-efka-budget-total">{fmtEuro(compare?.efka_budget)}</td>
                  <td className="px-4 py-3 text-right tabular" data-testid="budget-efka-actual-total">{fmtEuro(compare?.efka_actual)}</td>
                  <td className={`px-4 py-3 text-right tabular ${varTotal < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(varTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="budget-dept-card">
          <div className="px-5 py-4 border-b border-[#E3DED4]"><h2 className="text-lg font-medium text-[#45423C]">Ανά τμήμα</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-3">Τμήμα</th>
                  <th className="px-4 py-3 text-right">Προϋπ.</th>
                  <th className="px-4 py-3 text-right">Πραγμ.</th>
                  <th className="px-4 py-3 text-right">Φόρος Π/Υ</th><th className="px-4 py-3 text-right">Φόρος Πραγμ.</th><th className="px-4 py-3 text-right">ΕΦΚΑ Π/Υ</th>
                  <th className="px-4 py-3 text-right">ΕΦΚΑ Πραγμ.</th>
                  <th className="px-4 py-3 text-right">Απόκλιση</th>
                </tr>
              </thead>
              <tbody>
                {(compare?.department_rows || []).map((d) => (
                  <tr key={d.name} data-testid={`budget-dept-${d.name}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                    <td className="px-4 py-2 font-medium text-[#45423C]">{d.name}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtEuro(d.budget)}</td>
                    <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(d.actual)}</td>
                    <IkaCells r={d} />
                    <td className={`px-4 py-2 text-right tabular font-medium ${d.variance < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(d.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="budget-role-compare-card">
        <div className="px-5 py-4 border-b border-[#E3DED4]">
          <h2 className="text-lg font-medium text-[#45423C]">Προϋπολογισμός vs Πραγματικό ανά ρόλο</h2>
          <p className="text-xs text-[#6F6A60] mt-1">Ομαδοποίηση Τμήμα → Ρόλος. Κόκκινο = υπέρβαση.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Ρόλος</th>
                <th className="px-4 py-3 text-right">Προϋπ.</th>
                <th className="px-4 py-3 text-right">Πραγμ.</th>
                <th className="px-4 py-3 text-right">Φόρος Π/Υ</th><th className="px-4 py-3 text-right">Φόρος Πραγμ.</th><th className="px-4 py-3 text-right">ΕΦΚΑ Π/Υ</th>
                <th className="px-4 py-3 text-right">ΕΦΚΑ Πραγμ.</th>
                <th className="px-4 py-3 text-right">Απόκλιση</th>
              </tr>
            </thead>
            <tbody>
              {roleGroups.map((g) => (
                <React.Fragment key={g.department}>
                  <tr className="bg-[#F7F5F1] border-t border-[#E3DED4]" data-testid={`budget-role-group-${g.department}`}>
                    <td className="px-4 py-2 font-semibold text-[#45423C]">{g.department}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(g.budget)}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold text-[#6F6A60]">{fmtEuro(g.actual)}</td>
                    <IkaCells r={g} bold />
                    <td className={`px-4 py-2 text-right tabular font-semibold ${g.variance < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(g.variance)}</td>
                  </tr>
                  {g.roles.map((r) => (
                    <tr key={r.name} data-testid={`budget-role-row-${r.name}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                      <td className="px-4 py-2 pl-10 text-[#45423C]">{r.position}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtEuro(r.budget)}</td>
                      <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.actual)}</td>
                      <IkaCells r={r} />
                      <td className={`px-4 py-2 text-right tabular font-medium ${r.variance < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(r.variance)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="budget-emp-compare-card">
        <div className="px-5 py-4 border-b border-[#E3DED4]"><h2 className="text-lg font-medium text-[#45423C]">Πραγματικό vs Προϋπολογισμός ανά υπάλληλο</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Υπάλληλος</th>
                <th className="px-4 py-3">Τμήμα</th>
                <th className="px-4 py-3">Ρόλος</th>
                <th className="px-4 py-3 text-right">Προϋπ.</th>
                <th className="px-4 py-3 text-right">Πραγμ.</th>
                <th className="px-4 py-3 text-right">Φόρος Π/Υ</th><th className="px-4 py-3 text-right">Φόρος Πραγμ.</th><th className="px-4 py-3 text-right">ΕΦΚΑ Π/Υ</th>
                <th className="px-4 py-3 text-right">ΕΦΚΑ Πραγμ.</th>
                <th className="px-4 py-3 text-right">Απόκλιση</th>
              </tr>
            </thead>
            <tbody>
              {(compare?.employee_rows || []).map((r, i) => (
                <tr key={r.name + i} data-testid={`budget-emp-${r.name}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2 font-medium text-[#45423C]">{r.name}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.department || "—"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.position || "—"}</td>
                  <td className="px-4 py-2 text-right tabular">{fmtEuro(r.budget)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.actual)}</td>
                  <IkaCells r={r} />
                  <td className={`px-4 py-2 text-right tabular font-medium ${r.variance < 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{fmtEuro(r.variance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-[#6F6A60]" data-testid="budget-ika-note">Φόρος = ΦΜΥ (Π/Υ από τη στήλη ΦΜΥ του προϋπολογισμού, πραγμ. από τις μισθοδοσίες) — είναι μέσα στις αποδοχές. ΕΦΚΑ = σύνολο προς ΕΦΚΑ: εργοδοτικό ΙΚΑ + εισφορά εργαζομένου {Math.round((compare?.efka_employee_rate || 0) * 10000) / 100}% των αποδοχών. Η απόκλιση = αποδοχές + εργοδοτικό ΙΚΑ (χωρίς διπλομέτρηση). Εργοδοτικό ΙΚΑ πραγμ.: {compare?.ika_source === "monthly"
        ? `από τα εκκαθαριστικά ΕΦΚΑ (${(compare?.ika_entered_months || []).length} μήνες καταχωρημένοι· οι υπόλοιποι με εκτίμηση 25%), κατανεμημένο ανάλογα με τις αποδοχές του μήνα`
        : compare?.ika_source === "annual" ? "από το ετήσιο ΙΚΑ της Ετήσιας Αναφοράς, κατανεμημένο ανάλογα με τις αποδοχές"
          : "εκτίμηση 25% των αποδοχών — καταχωρήστε τα μηνιαία εκκαθαριστικά ΕΦΚΑ στην Ετήσια Αναφορά"}.</p>
    </div>
  );
}
